/**
 * Hilfsfunktionen für E-Mail-Komponenten
 */

/**
 * Formatiert ein Datum ins deutsche Format (DD.MM.YYYY)
 */
export const formatDatumDE = (datum: string | undefined): string => {
  if (!datum) return '';

  // Wenn bereits im deutschen Format (DD.MM.YYYY), direkt zurückgeben
  if (datum.includes('.')) {
    return datum;
  }

  // ISO-Format (YYYY-MM-DD) in deutsches Format konvertieren
  if (datum.includes('-')) {
    const parts = datum.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
  }

  return datum;
};

/**
 * Formatiert die Kategorien für bessere Lesbarkeit
 */
export const formatKategorie = (kategorie: string | undefined): string => {
  if (!kategorie) return '';

  // Entferne mögliche JSON-Zeichen oder Anführungszeichen
  const cleaned = kategorie.replace(/["{}[\]]/g, '').trim();

  // Formatiere die Kategorien lesbarer
  switch (cleaned) {
    case 'Wichtig':
      return 'Wichtig';
    case 'ZurKenntnis':
      return 'Zur Kenntnis';
    case 'Werbung':
      return 'Werbung';
    default:
      // Fallback: Füge Leerzeichen vor Großbuchstaben ein
      return cleaned.replace(/([A-Z])/g, ' $1').trim();
  }
};

/**
 * Erstellt eine ICS-Kalenderdatei und lädt sie herunter
 */
export const createCalendarEvent = (
  title: string,
  description: string,
  dateTime: string,
  solution: string = ''
) => {
  if (!dateTime) {
    alert('Kein Fälligkeitsdatum vorhanden. Bitte setzen Sie zuerst ein Datum.');
    return;
  }

  // Konvertiere datetime-local zu ICS-Format (YYYYMMDDTHHmmss)
  const date = new Date(dateTime);
  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  // Erstelle End-Datum (1 Stunde später)
  const endDate = new Date(date.getTime() + 60 * 60 * 1000);

  // Escape special characters für ICS
  const escapeICS = (text: string): string => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '');
  };

  // Erstelle vollständige Beschreibung mit Lösungsvorschlag
  let fullDescription = description;
  if (solution) {
    fullDescription += '\\n\\n--- LÖSUNGSVORSCHLAG ---\\n' + solution;
  }

  // Erstelle ICS-Inhalt
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Skeena//Ticket Calendar//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatDate(date)}`,
    `DTEND:${formatDate(endDate)}`,
    `DTSTAMP:${formatDate(new Date())}`,
    `UID:${Date.now()}@skeena.local`,
    `SUMMARY:${escapeICS(title)}`,
    `DESCRIPTION:${escapeICS(fullDescription)}`,
    'STATUS:CONFIRMED',
    'PRIORITY:5',
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    `DESCRIPTION:Erinnerung: ${escapeICS(title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  // Erstelle und downloade die Datei
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${title.substring(0, 50).replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '')}_${date.toISOString().split('T')[0]}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Bereinigt und formatiert Text (insbesondere KI-Zusammenfassungen)
 */
export const cleanText = (text: string | undefined): string => {
  if (!text) return '';

  let cleaned = text;

  // Entferne JSON-Formatierungen und Escape-Zeichen
  cleaned = cleaned
    .replace(/\\n/g, ' ')
    .replace(/\\r/g, ' ')
    .replace(/\\t/g, ' ')
    .replace(/\\/g, '')
    .replace(/["{}[\]]/g, '')
    .replace(/^["']|["']$/g, '');

  // Entferne tatsächliche Zeilenumbrüche und normalisiere Whitespace
  cleaned = cleaned
    .replace(/\r\n/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Entferne gängige KI-Präfixe falls vorhanden
  const prefixes = [
    'Zusammenfassung:',
    'Summary:',
    'Inhalt:',
    'Text:',
    'zusammenfassung:',
  ];

  for (const prefix of prefixes) {
    if (cleaned.startsWith(prefix)) {
      cleaned = cleaned.substring(prefix.length).trim();
    }
  }

  return cleaned;
};

/**
 * Konvertiert Markdown in HTML (nur inline-Formatierung)
 */
export const convertMarkdownToHtml = async (markdown: string): Promise<string> => {
  try {
    const { marked } = await import('marked');
    // Teile den Text in Zeilen auf
    const lines = markdown.split('\n');

    // Verarbeite jede Zeile mit parseInline
    const processedLines = lines.map(line => {
      if (line.trim() === '') {
        return '<br>';
      }
      return marked.parseInline(line) as string;
    });

    return processedLines.join('<br>');
  } catch (error) {
    console.error('Fehler bei Markdown-Konvertierung:', error);
    return markdown;
  }
};

/**
 * Konvertiert HTML in reinen Text
 */
export const convertHtmlToPlainText = (html: string): string => {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  } catch (error) {
    console.error('Fehler bei HTML-zu-Text-Konvertierung:', error);
    return html;
  }
};
